import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Upload,
  Button,
  Image,
  message,
  Spin,
  DatePicker,
  Modal,
} from "antd";
import { InboxOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";

const { Item } = Form;
const { confirm } = Modal;

const UpdateFssaiForm = () => {
  const { audit_id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isEditable, setIsEditable] = useState(false);

  const [fssaiFileUrl, setFssaiFileUrl] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [fileType, setFileType] = useState(""); // "image" or "pdf"
  const [inspectedProducts, setInspectedProducts] = useState([""]);
  const [equipmentUsed, setEquipmentUsed] = useState([""]);
  const [auditService, setAuditService] = useState();

  const apiUrl = process.env.REACT_APP_API_URL || ""; // ensure env var set
  const originalFileRef = useRef(""); // store original url to revert if needed

  useEffect(() => {
    if (!audit_id) return;
    setLoading(true);
    axios
      .get(`${apiUrl}/api/auditor/getAuditById/${audit_id}`)
      .then(({ data }) => {
        const d = data.data || {};
        console.log(data.data.service, "update Data");
        setAuditService(data.data.service);
        setFssaiFileUrl(d.fssai_image_url || "");
        originalFileRef.current = d.fssai_image_url || "";
        if (d.fssai_image_url?.endsWith?.(".pdf")) setFileType("pdf");
        else if (d.fssai_image_url) setFileType("image");
        setInspectedProducts(d.inspectedProducts?.length ? d.inspectedProducts : [""]);
        setEquipmentUsed(d.equipmentUsed?.length ? d.equipmentUsed : [""]);
        form.setFieldsValue({
          fssai_number: d.fssai_number || "",
          auditee_name: d.auditee_name || "",
          fostac_person: d.fostac_person || "",
          fostac_certificate_number: d.fostac_certificate_number || "",
          fostac_certificate_validity: d.fostac_certificate_validity ? dayjs(d.fostac_certificate_validity) : null,
        });
      })
      .catch((err) => {
        console.error("Fetch audit error:", err?.response?.data || err.message);
        message.error("Failed to fetch data");
      })
      .finally(() => setLoading(false));
  }, [audit_id, apiUrl, form]);

  // cleanup preview object URL on unmount / when preview changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch { }
      }
    };
  }, [previewUrl]);

  const handleFileUpload = ({ fileList }) => {
    if (fileList.length) {
      const file = fileList[0].originFileObj;
      // revoke prior preview url
      if (previewUrl) try { URL.revokeObjectURL(previewUrl); } catch { }
      setNewFile(file);
      setFileType(file.type === "application/pdf" ? "pdf" : "image");
      if (file.type !== "application/pdf") {
        setPreviewUrl(URL.createObjectURL(file));
      } else {
        setPreviewUrl(null);
      }
      // keep fssaiFileUrl untouched until save or explicit server delete
    } else {
      // cleared (rare path)
      if (previewUrl) try { URL.revokeObjectURL(previewUrl); } catch { }
      setNewFile(null);
      setPreviewUrl(null);
      setFileType("");
    }
  };

  // Confirm modal, then call delete on server (PUT with deleteImage=true)
  const confirmAndRemoveFile = () => {
    confirm({
      title: "Delete file?",
      icon: <ExclamationCircleOutlined />,
      content: "This will delete the saved file from server. This action can be undone only by re-uploading.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => removeFile(),
    });
  };

  const removeFile = async () => {
    // If local newFile selected (not saved to server), just clear selection
    if (newFile) {
      if (previewUrl) try { URL.revokeObjectURL(previewUrl); } catch { }
      setNewFile(null);
      setPreviewUrl(null);
      setFileType("");
      message.success("Selected file removed");
      return;
    }

    // If no server file, nothing to delete
    if (!fssaiFileUrl) {
      setPreviewUrl(null);
      setFileType("");
      message.success("File removed");
      return;
    }

    const prevUrl = fssaiFileUrl;
    // Optimistic UI clear
    setFssaiFileUrl("");
    setPreviewUrl(null);
    setFileType("");

    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("audit_id", audit_id);
      fd.append("deleteImage", "true");

      // include auth if required (uncomment and adjust token source)
      const token = localStorage.getItem("token"); // change key to your app's token key
      await axios.put(`${apiUrl}/api/auditor/updateFssaiDetails`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      originalFileRef.current = "";
      message.success("File deleted from server");
    } catch (error) {
      console.error("Delete file error:", error?.response?.data || error?.message);
      // revert UI
      setFssaiFileUrl(prevUrl);
      if (prevUrl?.endsWith?.(".pdf")) setFileType("pdf");
      else if (prevUrl) setFileType("image");
      message.error(error?.response?.data?.message || "Failed to delete file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    accept: ".jpg,.jpeg,.png,.pdf",
    maxCount: 1,
    beforeUpload: (file) => {
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
      const isValidType = allowedTypes.includes(file.type);
      const isValidSize = file.size / 1024 / 1024 <= 5;
      if (!isValidType) {
        message.error("Only .jpg, .jpeg, .png, or .pdf files allowed");
        return Upload.LIST_IGNORE;
      }
      if (!isValidSize) {
        message.error("File size must be ≤ 5 MB");
        return Upload.LIST_IGNORE;
      }
      return false; // prevent auto-upload
    },
    onRemove: () => {
      // user clicked the small X in the Upload list (should behave like clearing local selection)
      if (previewUrl) try { URL.revokeObjectURL(previewUrl); } catch { }
      setNewFile(null);
      setPreviewUrl(null);
      setFileType("");
    },
  };

  // dynamic list handlers (same as your code)
  const handleAddItem = (list, setter) => setter([...list, ""]);
  const handleRemoveItem = (list, setter, idx) => setter(list.filter((_, i) => i !== idx));
  const handleChangeItem = (list, setter, idx, value) => {
    const updated = [...list];
    updated[idx] = value;
    setter(updated);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("audit_id", audit_id);
      fd.append("fssai_number", values.fssai_number || "");
      fd.append("auditee_name", values.auditee_name || "");
      fd.append("fostac_person", values.fostac_person || "");
      fd.append("fostac_certificate_number", values.fostac_certificate_number || "");
      fd.append("fostac_certificate_validity", values.fostac_certificate_validity?.format("YYYY-MM-DD") || "");
      fd.append("inspectedProducts", JSON.stringify(inspectedProducts.filter(Boolean)));
      fd.append("equipmentUsed", JSON.stringify(equipmentUsed.filter(Boolean)));

      if (newFile) {
        fd.append("file", newFile);
      } else if (!fssaiFileUrl) {
        // when there is neither a new file nor existing server file -> tell backend to empty it
        fd.append("deleteImage", "true");
      }

      const token = localStorage.getItem("token"); // adjust if needed
      const res = await axios.put(`${apiUrl}/api/auditor/updateFssaiDetails`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      message.success("FSSAI details updated successfully!");
      setIsEditable(false);
      // update local state from response if provided
      if (res?.data?.data?.fssai_image_url !== undefined) {
        setFssaiFileUrl(res.data.data.fssai_image_url || "");
        originalFileRef.current = res.data.data.fssai_image_url || "";
        setFileType((res.data.data.fssai_image_url || "").endsWith(".pdf") ? "pdf" : (res.data.data.fssai_image_url ? "image" : ""));
      }
      setNewFile(null);
      if (previewUrl) try { URL.revokeObjectURL(previewUrl); } catch { }
      setPreviewUrl(null);
    } catch (error) {
      console.error("Update error:", error?.response?.data || error?.message);
      message.error(error?.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const location = useLocation();
  const firstSegment = location.pathname.split("/").filter(Boolean)[0];

  return (
    <div className="p-8">
      {loading && (
        <div className="fixed inset-0 flex justify-center items-center z-50 bg-white bg-opacity-75">
          <Spin size="large" />
        </div>
      )}

      <div className="flex justify-end mb-4">
        <Button
          type="primary"
          onClick={() => {
            if (isEditable) {
              setIsEditable(false);
              form.resetFields();
              // restore file & arrays from originalRef
              setFssaiFileUrl(originalFileRef.current || "");
              setNewFile(null);
              setPreviewUrl(null);
              setFileType(originalFileRef.current?.endsWith?.(".pdf") ? "pdf" : (originalFileRef.current ? "image" : ""));
            } else {
              setIsEditable(true);
            }
          }}
          disabled={!["draft", "modified"].includes(firstSegment)}
        >
          {isEditable ? "Cancel" : "Edit"}
        </Button>
      </div>

      <div className="bg-white rounded-xl p-6 shadow">
        <p className="text-lg mb-4 text-gray-800">
          The FSSAI license/Registration and Food Safety Display Board (FSDB) are displayed prominently.
        </p>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Item label="FSSAI License Number" name="fssai_number"
            rules={[{
              required: true,
              pattern: /^\d{14}$/, message: "Must be exactly 14 digits"
            }]}>
            <Input maxLength={14} disabled={!isEditable} placeholder="Enter 14-digit FSSAI License Number" />
          </Item>
          {/* {auditService && auditService.toUpperCase() !== "TPA" && (
            <Item label="FSSAI License Number" name="fssai_number"
              rules={[{
                required: true,
                pattern: /^\d{14}$/, message: "Must be exactly 14 digits"
              }]}>
              <Input maxLength={14} disabled={!isEditable} placeholder="Enter 14-digit FSSAI License Number" />
            </Item>
          )} */}

          <Item label="Auditee Name" name="auditee_name" rules={[{ required: true, message: "Please enter Auditee Name" }]}>
            <Input disabled={!isEditable} placeholder="Enter Auditee Name" />
          </Item>

          <Item label="Fostac Person" name="fostac_person" rules={[{ required: true, message: "Please enter Fostac Person Name" }]}>
            <Input disabled={!isEditable} placeholder="Enter Fostac Person Name" />
          </Item>

          <Item label="Fostac Certificate Number" name="fostac_certificate_number" rules={[{ required: true, message: "Please enter Fostac Certificate Number" }]}>
            <Input disabled={!isEditable} placeholder="Enter Fostac Certificate Number" />
          </Item>

          <Item label="Fostac Certificate Validity" name="fostac_certificate_validity" rules={[{ required: true, message: "Please select validity date" }]}>
            <DatePicker format="DD/MM/YYYY" className="w-full" disabled={!isEditable} placeholder="Select validity date" />
          </Item>

          <Item label="Inspected Product Names">
            {inspectedProducts.map((product, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <Input placeholder="Enter product name" value={product}
                  onChange={(e) => handleChangeItem(inspectedProducts, setInspectedProducts, idx, e.target.value)} disabled={!isEditable} />
                {inspectedProducts.length > 1 && isEditable && (
                  <Button danger icon={<DeleteOutlined />} size="small" onClick={() => handleRemoveItem(inspectedProducts, setInspectedProducts, idx)} />
                )}
              </div>
            ))}
            {isEditable && <Button type="dashed" onClick={() => handleAddItem(inspectedProducts, setInspectedProducts)}>+ Add Product</Button>}
          </Item>

          <Item label="Equipment Used">
            {equipmentUsed.map((equipment, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <Input placeholder="Enter equipment name" value={equipment}
                  onChange={(e) => handleChangeItem(equipmentUsed, setEquipmentUsed, idx, e.target.value)} disabled={!isEditable} />
                {equipmentUsed.length > 1 && isEditable && (
                  <Button danger icon={<DeleteOutlined />} size="small" onClick={() => handleRemoveItem(equipmentUsed, setEquipmentUsed, idx)} />
                )}
              </div>
            ))}
            {isEditable && <Button type="dashed" onClick={() => handleAddItem(equipmentUsed, setEquipmentUsed)}>+ Add Equipment</Button>}
          </Item>

          {isEditable && !fssaiFileUrl && !newFile && (
            <Item label="Upload FSSAI Certificate">
              <Upload.Dragger {...uploadProps} onChange={handleFileUpload} className="my-4">
                <InboxOutlined style={{ fontSize: 24, color: "#1890FF" }} />
                <p className="ant-upload-text">Drag or click to upload</p>
                <p className="ant-upload-hint">Max 5 MB – .jpg, .jpeg, .png, .pdf only</p>
              </Upload.Dragger>
            </Item>
          )}

          {(fssaiFileUrl || newFile) && (
            <div className="flex flex-col items-center my-4">
              {fileType === "image" && (
                <Image width={200} src={newFile ? previewUrl : fssaiFileUrl} style={{ border: "1px solid #ccc", borderRadius: 8 }} preview={{ mask: "Click to preview" }} />
              )}
              {fileType === "pdf" && (
                <a href={newFile ? URL.createObjectURL(newFile) : fssaiFileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">View PDF</a>
              )}
              {isEditable && (
                <Button danger icon={<DeleteOutlined />} onClick={confirmAndRemoveFile} className="mt-2">Remove File</Button>
              )}
            </div>
          )}

          {isEditable && (
            <div className="flex justify-end mt-6">
              <Button type="primary" htmlType="submit" loading={loading} size="large">Save Changes</Button>
            </div>
          )}
        </Form>
      </div>
    </div>
  );
};

export default UpdateFssaiForm;